import React, { useEffect, useState } from 'react';
import { PRESETS, GRANULARITIES, validateRange, granularityAllowed } from './salesUtils';

// Toggle group: plain buttons with aria-pressed inside a labelled role="group", so
// each option is a normal Tab stop and screen readers announce "pressed". A disabled
// option stays focusable (aria-disabled) so its hint can be read, but does nothing.
const Segmented = ({ label, options, value, onSelect, isDisabled = () => false, disabledHint }) => (
  <div role="group" aria-label={label} className="sales-segmented">
    {options.map((o) => {
      const disabled = isDisabled(o.key);
      return (
        <button
          key={o.key}
          type="button"
          aria-pressed={value === o.key}
          aria-disabled={disabled || undefined}
          title={disabled ? disabledHint : undefined}
          onClick={disabled ? undefined : () => onSelect(o.key)}
          className="sales-segment"
        >
          {o.label}
        </button>
      );
    })}
  </div>
);

// Presets + native date inputs + granularity toggle. Custom edits are validated
// inline; an invalid range is never sent upward, so nothing is fetched.
const DateRangeControl = ({ range, resolved, onChange, currencies, currency, onCurrencyChange }) => {
  const [draft, setDraft] = useState(resolved);
  const [error, setError] = useState(null);

  useEffect(() => {
    setDraft({ from: resolved.from, to: resolved.to });
    setError(null);
  }, [resolved.from, resolved.to]);

  const editDate = (field) => (e) => {
    const next = { ...draft, [field]: e.target.value };
    setDraft(next);
    const problem = validateRange(next.from, next.to);
    setError(problem);
    if (!problem) onChange({ preset: 'custom', from: next.from, to: next.to });
  };

  return (
    <div className="sales-card flex flex-col gap-4 md:flex-row md:flex-wrap md:items-end md:gap-x-6 md:gap-y-4 py-4 sm:py-5" data-testid="date-range-control">
      <div className="flex flex-col gap-2 min-w-0">
        <span className="sales-label">Range</span>
        <Segmented
          label="Date range"
          options={PRESETS}
          value={range.preset}
          onSelect={(preset) => onChange({ preset })}
        />
      </div>

      <div className="flex flex-col gap-2 min-w-0">
        <div className="flex gap-2">
          <label className="flex flex-col gap-2 flex-1 min-w-0 md:w-36">
            <span className="sales-label">From</span>
            <input type="date" value={draft.from} max={draft.to || undefined} onChange={editDate('from')} className="sales-input" aria-invalid={!!error} />
          </label>
          <label className="flex flex-col gap-2 flex-1 min-w-0 md:w-36">
            <span className="sales-label">To</span>
            <input type="date" value={draft.to} min={draft.from || undefined} onChange={editDate('to')} className="sales-input" aria-invalid={!!error} />
          </label>
        </div>
        {error && (
          <p className="text-battle-red-light text-xs" role="alert" data-testid="range-error">{error}</p>
        )}
      </div>

      <div className="flex flex-col gap-2 min-w-0">
        <span className="sales-label">Group by</span>
        <Segmented
          label="Granularity"
          options={GRANULARITIES}
          value={range.granularity}
          onSelect={(granularity) => onChange({ granularity })}
          isDisabled={(key) => !granularityAllowed(key, resolved.from, resolved.to)}
          disabledHint="Too many points for this range; pick a shorter range or a coarser grouping"
        />
      </div>

      {currencies && currencies.length > 1 && (
        <label className="flex flex-col gap-2 md:w-28">
          <span className="sales-label">Currency</span>
          <select value={currency} onChange={(e) => onCurrencyChange(e.target.value)} className="sales-input uppercase">
            {currencies.map((c) => <option key={c} value={c}>{c.toUpperCase()}</option>)}
          </select>
        </label>
      )}
    </div>
  );
};

export default DateRangeControl;
